import React, {useState, useRef, useContext, useEffect, useReducer} from 'react';
import BCMSTransistion from '../BCMSTransistion/main.jsx';
import BCMSDispatcher from "../../classes/BCMSDispatcher";
import "./styles.css"

// Creates context for managing "registered" nodes:
const BCMSRegistryContext = React.createContext(null);

/**
 *
 *  Components
 *
 */

// MAIN :: Makes "ACITVE" component available for defining what is clickable in a page:
// NOTE: Reducer is used to manage the state of what componets have been registered
export default function BCMSRegistry({ isActive, children }) {
  const dispatcher = BCMSDispatcher.init()
          .add("register", (state, action) => {
            // getRegisteredIndex :: * -> INTEGER
            // Return a new index that doesn't already exist within state
            const getRegisteredIndex = () => {
                    return state == undefined
                      ? 1
                      : state.reduce((result, item) => {
                          if(item.index >= result){ result = item.index + 1; }
                          return result;
                        }, 1);
                  };
            
            // Add the index to the passed payload and return state with the new item added
            const registeredItem = { ...action.payload, index: getRegisteredIndex() };
            return state == undefined ? [registeredItem] : state.concat(registeredItem);
          })
          .add("unregister", (state, action) => state.filter(item => item.index !== action.payload.index, []))
          .generate();

  const [state, dispatch] = useReducer(dispatcher);
  const dispatcherFN = (type, payload) => dispatch({ "type" : type, "payload" : payload });

  const enableRegistry = isActive ?? true;

  return (
    <BCMSRegistryContext.Provider value={{ "state": state, "dispatcher": dispatcherFN, "isActive" : enableRegistry }}>
      { enableRegistry === true ? disableClickContent(children, state) : children }
    </BCMSRegistryContext.Provider>
  );
}

// ACTIVE :: Wrapping this component around any other component or HTML will make that content "clickable":
// NOTE: This also registered nodes to be shown when a "non-clickable" node is clicked:
// NOTE: Supports "prop" for CSS "className" to style the container of "acitve" components with
export function BCMSActive(props) {
  const { state, dispatcher } = useBCMSRegistry();    // Retrieves state and dispatch from context
  return (
    <>
      {
        React.Children.map(props.children, (child, index) =>
          <BCMSTransistion
            inactive={props.className}
            active={props.className === undefined ? "show-active" : props.className + " show-active"}
            onClick={() => {
              state.forEach(registered=>{
                registered.set(false);
              })
            }}
            controller={(get, set, elem) => {
              // Register element to registry when component mounts:
              useEffect(() => {
                const properties = {
                        "elem" : elem.current,
                        "set" : set,
                      };

                if (elem !== null) {
                  dispatcher("register", properties);
                }
                // Removes element from registry when component unmounts:
                return () => dispatcher("unregister", properties);
              }, []);
            }}>
            <>
              <span className="notify"></span>
              { child }
            </>
          </BCMSTransistion>)
      }
    </>
  );
}


/**
 *
 *  Hooks
 *
 */

// Custom hook for accessing registry of "active" nodes:
export const useBCMSRegistry = () => React.useContext(BCMSRegistryContext);

/**
 *
 *  Other Functions
 *
 */

// Exposes "disableClickContent" function used to disable any "non-acitve" children of WIDGET or PAGE component
export {disableClickContent, isRegistered}


/**
 *
 *  Support Functions
 *
 */

// :: OBJECT, NODE -> BOOELAN
// Returns BOOLEAN for is node is registered, or a child of a nodes that regiseterd:
function isRegistered(registry, node) {
  for (let index in registry) {
    if (registry[index].elem.contains(node)) {
      return true;
    }
  }
  return false;
}

// :: [COMPONENT], OBJECT -> [COMPONNENT]
// Recursively binds disabled onclick event handler to all nodes that aren't ACTIVE components:
function disableClickContent(children, state) {
  return React.Children.map(children, (child)=>{
    if (!React.isValidElement(child)) {
      return child;
    }

    return child.type === BCMSActive
      ? child
      : React.cloneElement(child, {
        onClick:(evt) => {
          // Check is still needed if click is triggered from some ancestor of a BCMSActive component:
          if (!isRegistered(state, evt.target)) {
            evt.preventDefault()
            evt.stopPropagation();
            // Updates UI to show what's still clickable on the page:
            state.forEach(registered => {
              registered.set(true);
            })
          }
        }
      }, child.props !== undefined ? disableClickContent(child.props.children, state) : undefined)
  });
}
